import { SHOP_GRID_SLOT_KEY } from "./shopBlocks";
import { createDeterministicSiteBuilderBlockUuid } from "./siteBlockIdentity";
import type { ShopTemplateBlock } from "./types";

export const CATALOG_PRODUCT_PAGE_TYPE = "catalogProductPage";
export const CATALOG_PRODUCT_SLOT = "product";
export const CATALOG_PRODUCT_IMAGE_SLOT = "image";
export const CATALOG_CATEGORY_IMAGE_URLS_OPTION = "categoryImageUrls";

export const CATALOG_PRODUCT_DESIGNS = [
    "multiThumbnail",
    "multiChips",
    "multiCards",
    "multiGrid",
    "multiBento",
    "singleThumbnails",
    "singleCards",
    "singleGrid",
    "singleShowcase",
    "singleCondensed",
    "singleLarge",
    "editorialGrid",
    "utilityGrid",
    "boutiqueGrid",
    "classicBordered",
    "playfulCards",
] as const;

export type CatalogProductDesign = typeof CATALOG_PRODUCT_DESIGNS[number];

export const CATALOG_FILTER_STYLES = [
    "default",
    "editorialExpand",
    "utilityInline",
    "boutiqueSidebar",
    "structuredSidebar",
    "playfulSidebar",
] as const;

export type CatalogFilterStyle = typeof CATALOG_FILTER_STYLES[number];

export const CATALOG_FILTER_MODES = [
    "hidden",
    "sidebar",
    "offscreen",
    "inline",
] as const;

export const CATALOG_CATEGORY_STYLES = [
    "default",
    "bordered",
    "tabs",
    "pills",
    "images",
    "grid",
    "largeImages",
    "imageIndex",
    "playfulIndex",
] as const;

export const CATALOG_CATEGORIES_PLACEMENTS = [
    "top",
    "sidebar",
    "both",
] as const;

export const CATALOG_PRODUCT_RIBBONS = [
    "auto",
    "none",
    "soldOut",
    "outOfStock",
    "sale",
    "new",
] as const;

export type CatalogProductRibbon = typeof CATALOG_PRODUCT_RIBBONS[number];

export const CATALOG_PRODUCT_SORTS = [
    "recommended",
    "bestselling",
    "priceAsc",
    "priceDesc",
    "rating",
    "nameAsc",
    "newest",
] as const;

export type CatalogProductSort = typeof CATALOG_PRODUCT_SORTS[number];

export function catalogProductSort(value: string): CatalogProductSort {
    return CATALOG_PRODUCT_SORTS.includes(value as CatalogProductSort)
        ? value as CatalogProductSort
        : "recommended";
}

const catalogProductRuntimeSources = new Set([
    "catalogProductsSystem",
    "searchResultsSystem",
]);

export const CATALOG_PRODUCT_PAGE_DEFAULT_OPTIONS: Readonly<Record<string, unknown>> = {
    categoriesPlacement: "top",
    categoryStyle: "pills",
    collapseCategories: "false",
    collapseSidebarSections: "true",
    contentWidth: "regular",
    defaultSort: "recommended",
    desktopColumns: "4",
    filtersMode: "sidebar",
    mobileColumns: "2",
    productDesign: "multiGrid",
    showPriceFilter: "true",
    showTagsFilter: "true",
    toolbarSearch: "false",
    toolbarSort: "true",
};

export type CatalogCategoryStyle = typeof CATALOG_CATEGORY_STYLES[number];
export type CatalogFiltersMode = typeof CATALOG_FILTER_MODES[number];
export type CatalogCategoriesPlacement = typeof CATALOG_CATEGORIES_PLACEMENTS[number];
export type CatalogContentWidth = "regular" | "full";

export function catalogCategoryImageUrls(
    block: ShopTemplateBlock | null | undefined,
): readonly (string | null)[] {
    const value = block?.options?.[CATALOG_CATEGORY_IMAGE_URLS_OPTION];

    if (!Array.isArray(value)) return [];

    return value.map((item) => {
        if (typeof item !== "string") return null;

        const url = item.trim();

        return url === "" ? null : url;
    });
}

export type CatalogProductPageConfig = Readonly<{
    categoriesPlacement: CatalogCategoriesPlacement;
    categoryStyle: CatalogCategoryStyle;
    collapseCategories: boolean;
    collapseSidebarSections: boolean;
    contentWidth: CatalogContentWidth;
    defaultSort: CatalogProductSort;
    desktopColumns: number;
    filterStyle: CatalogFilterStyle;
    filtersMode: CatalogFiltersMode;
    mobileColumns: number;
    productDesign: CatalogProductDesign;
    showPriceFilter: boolean;
    showTagsFilter: boolean;
    toolbarSearch: boolean;
    toolbarSort: boolean;
}>;

function optionText(block: ShopTemplateBlock, key: string): string {
    const value = block.options?.[key];

    return typeof value === "string" ? value.trim() : "";
}

function optionBoolean(
    block: ShopTemplateBlock,
    key: string,
    fallback: boolean,
): boolean {
    const value = block.options?.[key];

    if (typeof value === "boolean") return value;
    if (value === "true" || value === "1" || value === 1) return true;
    if (value === "false" || value === "0" || value === 0) return false;

    return fallback;
}

function optionNumber(
    block: ShopTemplateBlock,
    key: string,
    fallback: number,
): number {
    const value = block.options?.[key];
    const number = typeof value === "number"
        ? value
        : typeof value === "string"
            ? Number.parseFloat(value)
            : Number.NaN;

    return Number.isFinite(number) ? number : fallback;
}

function clampedColumnCount(
    block: ShopTemplateBlock,
    key: string,
    fallback: number,
    maximum: number,
): number {
    return Math.min(
        maximum,
        Math.max(1, Math.round(optionNumber(block, key, fallback))),
    );
}

function catalogProductDesign(value: string): CatalogProductDesign {
    return CATALOG_PRODUCT_DESIGNS.includes(value as CatalogProductDesign)
        ? value as CatalogProductDesign
        : "multiGrid";
}

function catalogFilterStyle(value: string): CatalogFilterStyle {
    return CATALOG_FILTER_STYLES.includes(value as CatalogFilterStyle)
        ? value as CatalogFilterStyle
        : "default";
}

function catalogCategoryStyle(value: string): CatalogCategoryStyle {
    return CATALOG_CATEGORY_STYLES.includes(value as CatalogCategoryStyle)
        ? value as CatalogCategoryStyle
        : "pills";
}

export function catalogProductPageConfig(
    block: ShopTemplateBlock | null | undefined,
): CatalogProductPageConfig {
    const source = block ?? { type: CATALOG_PRODUCT_PAGE_TYPE };
    const categoryStyle = optionText(source, "categoryStyle");
    const design = optionText(source, "productDesign");
    const filterStyle = optionText(source, "filterStyle");
    const filtersMode = optionText(source, "filtersMode");
    const defaultSort = optionText(source, "defaultSort");

    return {
        categoriesPlacement: CATALOG_CATEGORIES_PLACEMENTS.includes(
            optionText(source, "categoriesPlacement") as CatalogCategoriesPlacement,
        )
            ? optionText(source, "categoriesPlacement") as CatalogCategoriesPlacement
            : "top",
        categoryStyle: catalogCategoryStyle(categoryStyle),
        collapseCategories: optionBoolean(source, "collapseCategories", false),
        collapseSidebarSections: optionBoolean(
            source,
            "collapseSidebarSections",
            true,
        ),
        contentWidth: optionText(source, "contentWidth") === "full"
            ? "full"
            : "regular",
        defaultSort: catalogProductSort(defaultSort),
        desktopColumns: clampedColumnCount(source, "desktopColumns", 4, 6),
        filterStyle: catalogFilterStyle(filterStyle),
        filtersMode: CATALOG_FILTER_MODES.includes(filtersMode as CatalogFiltersMode)
            ? filtersMode as CatalogFiltersMode
            : "sidebar",
        mobileColumns: clampedColumnCount(source, "mobileColumns", 2, 2),
        productDesign: catalogProductDesign(design),
        showPriceFilter: optionBoolean(source, "showPriceFilter", true),
        showTagsFilter: optionBoolean(source, "showTagsFilter", true),
        toolbarSearch: optionBoolean(source, "toolbarSearch", false),
        toolbarSort: optionBoolean(source, "toolbarSort", true),
    };
}

function derivedPrototypeUuid(
    parentUuid: string | undefined,
    slot: string,
): string | undefined {
    return parentUuid
        ? createDeterministicSiteBuilderBlockUuid(parentUuid, `commerce.catalog.${slot}`)
        : undefined;
}

function defaultProductImageTemplate(productUuid?: string): ShopTemplateBlock {
    return {
        type: "image",
        ...(derivedPrototypeUuid(productUuid, "image")
            ? { uuid: derivedPrototypeUuid(productUuid, "image") }
            : {}),
        content: {
            alt: "",
        },
        options: {
            documentRole: "productImage",
            objectFit: "cover",
            stretch: "true",
        },
    };
}

function normalizeProductTemplate(
    block: ShopTemplateBlock,
    productPageUuid?: string,
): ShopTemplateBlock {
    const images = block.slots?.[CATALOG_PRODUCT_IMAGE_SLOT] ?? [];
    const productUuid = block.uuid ?? derivedPrototypeUuid(productPageUuid, "product");
    const image = images[0] ?? defaultProductImageTemplate(productUuid);
    const normalizedImage: ShopTemplateBlock = {
        ...image,
        children: undefined,
        options: {
            documentRole: "productImage",
            objectFit: "cover",
            stretch: "true",
            ...(image.options ?? {}),
        },
        slots: undefined,
    };

    return {
        ...block,
        ...(productUuid ? { uuid: productUuid } : {}),
        options: {
            documentRole: "productCardTemplate",
            ribbon: "auto",
            ...(block.options ?? {}),
        },
        children: undefined,
        slots: {
            [CATALOG_PRODUCT_IMAGE_SLOT]: [normalizedImage],
        },
    };
}

function defaultProductTemplate(productPageUuid?: string): ShopTemplateBlock {
    return normalizeProductTemplate({
        type: "product",
    }, productPageUuid);
}

function normalizeProductPage(
    block: ShopTemplateBlock,
    runtimeSource?: string,
): ShopTemplateBlock {
    const products = block.slots?.[CATALOG_PRODUCT_SLOT] ?? [];
    const product = normalizeProductTemplate(
        products[0] ?? defaultProductTemplate(block.uuid),
        block.uuid,
    );

    return {
        ...block,
        type: CATALOG_PRODUCT_PAGE_TYPE,
        options: {
            ...CATALOG_PRODUCT_PAGE_DEFAULT_OPTIONS,
            ...(runtimeSource ? { runtimeSource } : {}),
            ...(block.options ?? {}),
        },
        children: undefined,
        slots: {
            [CATALOG_PRODUCT_SLOT]: [product],
        },
    };
}

/**
 * Upgrades a legacy shop product-list anchor into a persistent
 * Product page -> Product -> Image design prototype. Real products remain
 * runtime catalog entities; the nested blocks only own listing presentation.
 */
export function normalizeCatalogProductPageStructure(
    region: ShopTemplateBlock,
): ShopTemplateBlock {
    if (region.type !== "richContent") {
        return region;
    }

    const items = region.slots?.[SHOP_GRID_SLOT_KEY];

    if (!Array.isArray(items)) {
        return region;
    }

    let changed = false;
    const normalizedItems = items.map((item) => {
        const authoredRuntimeSource = optionText(item, "runtimeSource");
        const commerceRuntimeRole = optionText(item, "commerceRuntimeRole");
        const documentRole = optionText(item, "documentRole");
        const legacyCatalogAnchor = (
            commerceRuntimeRole === "commerce.catalog.products"
            && documentRole === "systemProductList"
        );

        if (
            item.type !== CATALOG_PRODUCT_PAGE_TYPE
            && !catalogProductRuntimeSources.has(authoredRuntimeSource)
            && !legacyCatalogAnchor
        ) {
            return item;
        }

        changed = true;

        return normalizeProductPage(
            item,
            authoredRuntimeSource || undefined,
        );
    });

    if (!changed) {
        return region;
    }

    return {
        ...region,
        slots: {
            ...(region.slots ?? {}),
            [SHOP_GRID_SLOT_KEY]: normalizedItems,
        },
    };
}

export function catalogProductPage(
    region: ShopTemplateBlock | null | undefined,
): ShopTemplateBlock | null {
    const items = region?.slots?.[SHOP_GRID_SLOT_KEY] ?? [];

    return items.find((item) => item.type === CATALOG_PRODUCT_PAGE_TYPE) ?? null;
}

export function catalogProductTemplate(
    productPage: ShopTemplateBlock | null | undefined,
): ShopTemplateBlock | null {
    return productPage?.slots?.[CATALOG_PRODUCT_SLOT]?.[0] ?? null;
}

export function catalogProductImageTemplate(
    product: ShopTemplateBlock | null | undefined,
): ShopTemplateBlock | null {
    return product?.slots?.[CATALOG_PRODUCT_IMAGE_SLOT]?.[0] ?? null;
}

function findProductPageByRuntimeSource(
    block: ShopTemplateBlock,
    runtimeSource: string,
): ShopTemplateBlock | null {
    if (
        block.type === CATALOG_PRODUCT_PAGE_TYPE
        && (
            optionText(block, "runtimeSource") === runtimeSource
            || (
                optionText(block, "commerceRuntimeRole") === "commerce.catalog.products"
                && optionText(block, "documentRole") === "systemProductList"
            )
        )
    ) {
        return block;
    }

    for (const child of block.children ?? []) {
        const match = findProductPageByRuntimeSource(child, runtimeSource);

        if (match) return match;
    }

    for (const children of Object.values(block.slots ?? {})) {
        for (const child of children) {
            const match = findProductPageByRuntimeSource(child, runtimeSource);

            if (match) return match;
        }
    }

    return null;
}

/**
 * Resolves the authored default used for the editor's data request. The
 * backend applies the same precedence for published storefront requests.
 */
export function catalogProductPageDefaultSort(
    blocks: ShopTemplateBlock[],
    runtimeSource: "catalogProductsSystem" | "searchResultsSystem",
): CatalogProductSort {
    const productPage = blocks
        .map((block) => findProductPageByRuntimeSource(block, runtimeSource))
        .find((block): block is ShopTemplateBlock => block !== null);
    const configured = productPage ? optionText(productPage, "defaultSort") : "";

    return CATALOG_PRODUCT_SORTS.includes(configured as CatalogProductSort)
        ? configured as CatalogProductSort
        : "recommended";
}
